import React, { useState, useRef, useEffect } from 'react';
import { aiApi } from '../../services/api';
import toast from 'react-hot-toast';
import {
  CheckCircleIcon,
  ArrowPathIcon,
  SparklesIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  ClipboardDocumentCheckIcon,
  LockClosedIcon,
} from 'components/icons';
import { CheckCircleIcon as CheckCircleSolidIcon } from 'components/icons/solid';

interface ChecklistItem {
  id: string;
  category: string;
  title: string;
  description: string;
  source: 'user' | 'team' | 'both';
  linkedSection: string | null;
  orderIndex: number;
  isRequired: boolean;
  isCompleted: boolean;
  completedAt: string | null;
  inputType: string | null;
  inputLabel: string | null;
  inputValue: string | null;
  inputOptions: string[] | null;
  metadata: { solicitationRef?: string; reasoning?: string } | null;
}

interface ChecklistProgress {
  total: number;
  completed: number;
  allDone: boolean;
}

interface Props {
  opportunityId: string;
  checklist: ChecklistItem[];
  progress: ChecklistProgress | null;
  proposalId: string | null;
  initialGenerationStatus?: string | null;
  onChecklistUpdate: (checklist: ChecklistItem[], progress: ChecklistProgress, proposalId: string) => void;
  onAllComplete: () => void;
}

const CATEGORY_CONFIG: Record<string, { label: string; color: string; bgColor: string; icon: string }> = {
  eligibility: { label: 'Eligibility', color: 'text-purple-700', bgColor: 'bg-purple-50 border-purple-200', icon: '🔑' },
  vendor_sourcing: { label: 'Vendor / Subcontractor Sourcing', color: 'text-blue-700', bgColor: 'bg-blue-50 border-blue-200', icon: '🤝' },
  personnel: { label: 'Personnel & Staffing', color: 'text-indigo-700', bgColor: 'bg-indigo-50 border-indigo-200', icon: '👥' },
  pricing: { label: 'Pricing & Cost', color: 'text-green-700', bgColor: 'bg-green-50 border-green-200', icon: '💰' },
  past_performance: { label: 'Past Performance', color: 'text-amber-700', bgColor: 'bg-amber-50 border-amber-200', icon: '📋' },
  technical: { label: 'Technical Requirements', color: 'text-cyan-700', bgColor: 'bg-cyan-50 border-cyan-200', icon: '⚙️' },
  compliance: { label: 'Compliance & Certifications', color: 'text-red-700', bgColor: 'bg-red-50 border-red-200', icon: '✅' },
  documentation: { label: 'Documentation & Forms', color: 'text-orange-700', bgColor: 'bg-orange-50 border-orange-200', icon: '📄' },
  timeline: { label: 'Timeline & Schedule', color: 'text-teal-700', bgColor: 'bg-teal-50 border-teal-200', icon: '📅' },
  submission: { label: 'Submission', color: 'text-pink-700', bgColor: 'bg-pink-50 border-pink-200', icon: '🚀' },
};

const SOURCE_CONFIG: Record<string, { label: string; className: string; tooltip: string }> = {
  user: { label: 'You', className: 'bg-green-50 text-green-700 border-green-200', tooltip: 'You handle this — no outside input needed' },
  team: { label: 'From Team', className: 'bg-blue-50 text-blue-700 border-blue-200', tooltip: 'Get this from your subs, vendors, or partners' },
  both: { label: 'You + Team', className: 'bg-purple-50 text-purple-700 border-purple-200', tooltip: 'You coordinate, but need input from your team' },
};

const ProposalChecklist: React.FC<Props> = ({
  opportunityId,
  checklist,
  progress,
  proposalId,
  initialGenerationStatus,
  onChecklistUpdate,
  onAllComplete,
}) => {
  const [generating, setGenerating] = useState(false);
  const [togglingItem, setTogglingItem] = useState<string | null>(null);
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set(['eligibility', 'vendor_sourcing', 'pricing', 'compliance', 'submission']));
  const [editingValue, setEditingValue] = useState<string | null>(null);
  const [pendingValues, setPendingValues] = useState<Record<string, string>>({});
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Clean up polling on unmount
  useEffect(() => {
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, []);

  // Resume polling if generation was already in progress when we mounted
  useEffect(() => {
    if (initialGenerationStatus === 'generating') {
      setGenerating(true);
      startPolling();
    }
  // startPolling is redefined every render and owns its own interval; listing it would tear down and restart polling continuously.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialGenerationStatus]);

  const startPolling = () => {
    if (pollRef.current) clearInterval(pollRef.current);
    pollRef.current = setInterval(async () => {
      try {
        const res = await aiApi.getChecklist(opportunityId);
        const data = res.data.data;

        if (data?.generationStatus === 'done' && data.checklist?.length > 0) {
          // Generation complete — load items
          if (pollRef.current) clearInterval(pollRef.current);
          pollRef.current = null;
          setGenerating(false);
          onChecklistUpdate(data.checklist, data.progress, data.proposalId);
          const cats = new Set<string>(data.checklist.map((i: ChecklistItem) => i.category));
          setExpandedCategories(cats);
          toast.success(`Generated ${data.checklist.length} checklist items`);
        } else if (data?.generationStatus === 'failed') {
          // Generation failed
          if (pollRef.current) clearInterval(pollRef.current);
          pollRef.current = null;
          setGenerating(false);
          toast.error('Checklist generation failed. Please try again.');
        }
        // If still 'generating', keep polling
      } catch {
        // Network error during poll — keep trying
      }
    }, 3000);
  };

  const handleGenerate = async () => {
    if (generating) return;
    setGenerating(true);
    try {
      await aiApi.generateChecklist(opportunityId);
      // Backend returns immediately — start polling for results
      startPolling();
    } catch (error: any) {
      setGenerating(false);
      toast.error(error.response?.data?.error || 'Failed to start checklist generation');
    }
  };

  const handleToggle = async (item: ChecklistItem) => {
    // If item has a required data entry field and no value, don't allow completion
    if (!item.isCompleted && item.inputType && !item.inputValue && !pendingValues[item.id]) {
      toast.error('Please fill in the required information before checking this off');
      setEditingValue(item.id);
      return;
    }

    setTogglingItem(item.id);
    try {
      // Save pending value first if exists
      if (pendingValues[item.id]) {
        await aiApi.updateChecklistValue(item.id, pendingValues[item.id]);
        setPendingValues(prev => {
          const next = { ...prev };
          delete next[item.id];
          return next;
        });
      }

      const response = await aiApi.toggleChecklistItem(item.id);
      const { item: updatedItem, progress: newProgress } = response.data.data;

      const updatedChecklist = checklist.map(i =>
        i.id === updatedItem.id ? updatedItem : i
      );
      onChecklistUpdate(updatedChecklist, newProgress, proposalId!);

      if (newProgress.allDone) {
        onAllComplete();
      }
    } catch (error: any) {
      toast.error('Failed to update item');
    } finally {
      setTogglingItem(null);
    }
  };

  const handleValueSave = async (itemId: string) => {
    const value = pendingValues[itemId];
    if (value === undefined) return;

    try {
      // Trust the server's version of the item rather than patching a guess:
      // saving a value now also completes the item, and this used to write back
      // only inputValue with the OLD progress object — so the item stayed
      // visibly unchecked and the progress bar never moved, which is what kept
      // generation looking locked after every field had been filled in.
      const res = await aiApi.updateChecklistValue(itemId, value);
      const saved = res?.data?.data?.item;

      const updatedChecklist = checklist.map(i =>
        i.id === itemId
          ? { ...i, ...(saved || { inputValue: value, isCompleted: value.trim().length > 0 }) }
          : i
      );
      const completed = updatedChecklist.filter(i => i.isCompleted).length;
      const nextProgress = {
        total: updatedChecklist.length,
        completed,
        allDone: completed === updatedChecklist.length,
      };

      onChecklistUpdate(updatedChecklist, nextProgress, proposalId!);
      setEditingValue(null);
      setPendingValues(prev => {
        const next = { ...prev };
        delete next[itemId];
        return next;
      });
      toast.success(nextProgress.allDone ? 'Saved — proposal generation unlocked' : 'Saved');
      if (nextProgress.allDone) onAllComplete();
    } catch {
      toast.error('Failed to save value');
    }
  };

  // Group items by category
  const grouped = checklist.reduce<Record<string, ChecklistItem[]>>((acc, item) => {
    if (!acc[item.category]) acc[item.category] = [];
    acc[item.category].push(item);
    return acc;
  }, {});

  // Sort categories by the lowest orderIndex in each group
  const sortedCategories = Object.keys(grouped).sort(
    (a, b) => (grouped[a][0]?.orderIndex ?? 0) - (grouped[b][0]?.orderIndex ?? 0)
  );

  const toggleCategory = (cat: string) => {
    setExpandedCategories(prev => {
      const next = new Set(prev);
      if (next.has(cat)) next.delete(cat);
      else next.add(cat);
      return next;
    });
  };

  const progressPercent = progress ? Math.round((progress.completed / progress.total) * 100) : 0;

  // Empty state — no checklist yet
  if (checklist.length === 0) {
    return (
      <div className="bg-surface shadow rounded-lg p-6">
        <div className="text-center">
          <ClipboardDocumentCheckIcon className="mx-auto h-12 w-12 text-content-subtle" />
          <h3 className="mt-3 text-base font-semibold text-content">Bid Readiness Checklist</h3>
          <p className="mt-2 text-sm text-content-muted max-w-sm mx-auto">
            Let AI analyze the solicitation and create a specific checklist of everything you need to do before you can submit a competitive bid.
          </p>
          <button
            onClick={handleGenerate}
            disabled={generating}
            className="mt-4 inline-flex items-center gap-2 px-4 py-2.5 bg-brand-600 text-white rounded-lg hover:bg-brand-700 disabled:opacity-50 text-sm font-medium transition-colors"
          >
            {generating ? (
              <>
                <ArrowPathIcon className="h-4 w-4 animate-spin" />
                Analyzing solicitation...
              </>
            ) : (
              <>
                <SparklesIcon className="h-4 w-4" />
                Generate Bid Checklist
              </>
            )}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-surface shadow rounded-lg overflow-hidden">
      {/* Header with progress */}
      <div className="px-5 py-4 border-b border-border">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <ClipboardDocumentCheckIcon className="h-5 w-5 text-brand-600" />
            <h3 className="font-semibold text-content">Bid Readiness Checklist</h3>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-xs font-medium text-content-muted">
              {progress?.completed}/{progress?.total} complete
            </span>
            <button
              onClick={handleGenerate}
              disabled={generating}
              className="text-xs text-brand-600 hover:text-brand-700 flex items-center gap-1"
              title="Regenerate checklist"
            >
              <ArrowPathIcon className={`h-3 w-3 ${generating ? 'animate-spin' : ''}`} />
              {generating ? 'Regenerating...' : 'Regenerate'}
            </button>
          </div>
        </div>

        {/* Progress bar */}
        <p className="text-xs text-content-muted mb-2">
          Filling in an item marks it done. Items with nothing to fill in are marked done by
          clicking the circle. Generation unlocks when all of them are done.
        </p>
        <div className="w-full bg-gray-200 rounded-full h-2.5">
          <div
            className={`h-2.5 rounded-full transition-all duration-500 ${
              progress?.allDone ? 'bg-green-500' : 'bg-brand-600'
            }`}
            style={{ width: `${progressPercent}%` }}
          />
        </div>
        {progress?.allDone && (
          <p className="mt-2 text-xs text-green-600 font-medium flex items-center gap-1">
            <CheckCircleIcon className="h-4 w-4" />
            All items complete — you're ready to generate your proposal!
          </p>
        )}
        {!progress?.allDone && progress && progress.completed > 0 && (
          <p className="mt-2 text-xs text-content-muted">
            {progress.total - progress.completed} items remaining before proposal generation unlocks
          </p>
        )}
      </div>

      {/* Checklist items grouped by category */}
      <div className="divide-y divide-border">
        {sortedCategories.map(category => {
          const items = grouped[category];
          const config = CATEGORY_CONFIG[category] || { label: category, color: 'text-content', bgColor: 'bg-surface-sunk border-border', icon: '📌' };
          const isExpanded = expandedCategories.has(category);
          const catCompleted = items.filter(i => i.isCompleted).length;
          const catTotal = items.length;
          const allCatDone = catCompleted === catTotal;

          return (
            <div key={category}>
              {/* Category header */}
              <button
                onClick={() => toggleCategory(category)}
                className={`w-full px-5 py-3 flex items-center justify-between hover:bg-surface-sunk transition-colors ${
                  allCatDone ? 'bg-green-50/50' : ''
                }`}
              >
                <div className="flex items-center gap-2">
                  {isExpanded ? (
                    <ChevronDownIcon className="h-4 w-4 text-content-subtle" />
                  ) : (
                    <ChevronRightIcon className="h-4 w-4 text-content-subtle" />
                  )}
                  <span className="text-sm">{config.icon}</span>
                  <span className={`text-sm font-medium ${allCatDone ? 'text-green-700' : 'text-content'}`}>
                    {config.label}
                  </span>
                </div>
                <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${
                  allCatDone
                    ? 'bg-green-100 text-green-700'
                    : catCompleted > 0
                      ? 'bg-brand-100 text-brand-700'
                      : 'bg-surface-sunk text-content-muted'
                }`}>
                  {catCompleted}/{catTotal}
                </span>
              </button>

              {/* Items */}
              {isExpanded && (
                <div className="pb-2">
                  {items.map(item => (
                    <div
                      key={item.id}
                      className={`mx-3 mb-2 rounded-lg border transition-all ${
                        item.isCompleted
                          ? 'bg-green-50/60 border-green-200'
                          : 'bg-surface border-border hover:border-border-strong'
                      }`}
                    >
                      <div className="px-4 py-3">
                        {/* Title row with checkbox */}
                        <div className="flex items-start gap-3">
                          <button
                            onClick={() => handleToggle(item)}
                            disabled={togglingItem === item.id}
                            className="flex-shrink-0 mt-0.5"
                            aria-label={item.isCompleted ? `Mark "${item.title}" not done` : `Mark "${item.title}" done`}
                            title={
                              item.inputType
                                ? 'Saving your answer marks this done — or click to mark it done now'
                                : item.isCompleted
                                  ? 'Click to mark not done'
                                  : 'Click to mark done'
                            }
                          >
                            {togglingItem === item.id ? (
                              <ArrowPathIcon className="h-5 w-5 text-brand-500 animate-spin" />
                            ) : item.isCompleted ? (
                              <CheckCircleSolidIcon className="h-5 w-5 text-green-500" />
                            ) : (
                              <span className="block w-5 h-5 rounded-full border-2 border-border-strong hover:border-brand-500 transition-colors" />
                            )}
                          </button>
                          <div className="flex-1 min-w-0">
                            <p className={`text-sm font-medium ${item.isCompleted ? 'text-green-700 line-through' : 'text-content'}`}>
                              {item.title}
                            </p>
                            <p className="text-xs text-content-muted mt-1">{item.description}</p>

                            {/* Metadata tags */}
                            <div className="flex flex-wrap gap-1.5 mt-2">
                              {item.source && SOURCE_CONFIG[item.source] && (
                                <span
                                  title={SOURCE_CONFIG[item.source].tooltip}
                                  className={`inline-flex items-center text-xs px-2 py-0.5 rounded-full border cursor-default ${SOURCE_CONFIG[item.source].className}`}
                                >
                                  {SOURCE_CONFIG[item.source].label}
                                </span>
                              )}
                              {item.linkedSection && (
                                <span className="inline-flex items-center text-xs px-2 py-0.5 rounded-full bg-brand-50 text-brand-700 border border-brand-200">
                                  Feeds into: {item.linkedSection}
                                </span>
                              )}
                              {item.metadata?.solicitationRef && (
                                <span className="inline-flex items-center text-xs px-2 py-0.5 rounded-full bg-surface-sunk text-content-muted">
                                  📖 {item.metadata.solicitationRef}
                                </span>
                              )}
                            </div>

                            {/* Data entry field (Phase 2) */}
                            {item.inputType && (
                              <div className="mt-3 p-3 bg-surface-sunk rounded-lg border border-border">
                                <label className="block text-xs font-medium text-content mb-1.5">
                                  {item.inputLabel || 'Enter value'}
                                </label>
                                {item.inputType === 'textarea' ? (
                                  <textarea
                                    value={pendingValues[item.id] ?? item.inputValue ?? ''}
                                    onChange={e => setPendingValues(prev => ({ ...prev, [item.id]: e.target.value }))}
                                    onFocus={() => setEditingValue(item.id)}
                                    placeholder={item.inputLabel || 'Enter details...'}
                                    rows={3}
                                    className="w-full text-sm border border-border-strong rounded-md px-3 py-2 focus:ring-1 focus:ring-brand-500 focus:border-brand-500"
                                    disabled={item.isCompleted}
                                  />
                                ) : item.inputType === 'select' && item.inputOptions ? (
                                  <select
                                    value={pendingValues[item.id] ?? item.inputValue ?? ''}
                                    onChange={e => {
                                      setPendingValues(prev => ({ ...prev, [item.id]: e.target.value }));
                                      setEditingValue(item.id);
                                    }}
                                    className="w-full text-sm border border-border-strong rounded-md px-3 py-2 focus:ring-1 focus:ring-brand-500 focus:border-brand-500"
                                    disabled={item.isCompleted}
                                  >
                                    <option value="">Select...</option>
                                    {item.inputOptions.map((opt: string) => (
                                      <option key={opt} value={opt}>{opt}</option>
                                    ))}
                                  </select>
                                ) : (
                                  <input
                                    type={item.inputType === 'currency' ? 'text' : item.inputType === 'number' ? 'number' : item.inputType === 'date' ? 'date' : 'text'}
                                    value={pendingValues[item.id] ?? item.inputValue ?? ''}
                                    onChange={e => setPendingValues(prev => ({ ...prev, [item.id]: e.target.value }))}
                                    onFocus={() => setEditingValue(item.id)}
                                    placeholder={
                                      item.inputType === 'currency' ? '$0.00'
                                        : item.inputType === 'date' ? 'YYYY-MM-DD'
                                          : item.inputLabel || 'Enter value...'
                                    }
                                    className="w-full text-sm border border-border-strong rounded-md px-3 py-2 focus:ring-1 focus:ring-brand-500 focus:border-brand-500"
                                    disabled={item.isCompleted}
                                  />
                                )}
                                {editingValue === item.id && pendingValues[item.id] !== undefined && (
                                  <button
                                    onClick={() => handleValueSave(item.id)}
                                    className="mt-2 text-xs px-3 py-1 bg-brand-600 text-white rounded-md hover:bg-brand-700"
                                  >
                                    Save
                                  </button>
                                )}
                                {item.isCompleted && item.inputValue && (
                                  <p className="mt-1 text-xs text-green-600">Saved: {item.inputValue}</p>
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Gating message */}
      {!progress?.allDone && (
        <div className="px-5 py-4 bg-amber-50 border-t border-amber-200">
          <div className="flex items-start gap-2">
            <LockClosedIcon className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-medium text-amber-800">Proposal generation is locked</p>
              <p className="text-xs text-amber-600 mt-0.5">
                {progress ? progress.total - progress.completed : ''} item
                {progress && progress.total - progress.completed === 1 ? '' : 's'} still to mark done.
                Filling in an item marks it done; for items with nothing to fill in, click the
                circle beside it. This keeps a generated proposal from resting on gaps you
                haven't checked.
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProposalChecklist;
